import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, FileText, Maximize2 } from "lucide-react";
import { PRODUCTS } from "@/data/products";
import { productJsonLd } from "@/data/jsonld";
import { useQuote } from "@/context/QuoteContext";
import { useSeo } from "@/lib/seo";
import ImageLightbox from "@/components/ImageLightbox";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";
import { Eyebrow } from "@/components/PageHeader";

const ProductImage = ({ product }) => {
  const [zoom, setZoom] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setZoom(true)}
        data-testid="product-detail-zoom"
        aria-label={`Ingrandisci ${product.name}`}
        style={product.imageBg ? { background: product.imageBg } : undefined}
        className="group relative block aspect-square w-full cursor-zoom-in overflow-hidden rounded-xl border border-white/10 bg-gradient-to-b from-[#e2e2e2] to-[#c4c4c4]"
      >
        <img src={product.image} alt={product.alt} data-testid="product-detail-image" className="h-full w-full object-contain p-6" />
        <span className="absolute bottom-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-ink/70 text-white backdrop-blur">
          <Maximize2 className="h-4 w-4" />
        </span>
      </button>
      <ImageLightbox product={product} open={zoom} onOpenChange={setZoom} />
    </>
  );
};

const QuoteButton = ({ product }) => {
  const { requestQuote, items } = useQuote();
  const inList = items.some((p) => p.id === product.id);
  return (
    <button
      data-testid="product-detail-quote-button"
      onClick={() => requestQuote(product)}
      className={`inline-flex items-center justify-center gap-3 rounded-md px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-white transition-colors ${
        inList ? "bg-emerald-600 hover:bg-emerald-700" : "bg-brand hover:bg-brand-hover"
      }`}
    >
      {inList ? <Check className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
      {inList ? "Nel preventivo" : "Richiedi preventivo"}
    </button>
  );
};

const ProductInfo = ({ product }) => (
  <div>
    <span data-testid="product-detail-code" className="rounded border border-brand/30 bg-brand/10 px-2 py-0.5 font-display text-xs font-bold tracking-wider text-brand">
      {product.code}
    </span>
    <h1 data-testid="product-detail-name" className="mt-5 font-display text-4xl font-extrabold uppercase leading-[0.95] text-white md:text-6xl">
      {product.name}
    </h1>
    <p className="mt-3 text-xs uppercase tracking-[0.2em] text-white/40">{product.category}</p>
    <div className="mt-8 space-y-4 text-base leading-relaxed text-white/65">
      {product.description.map((d) => (
        <p key={d.slice(0, 24)}>{d}</p>
      ))}
    </div>
    <h2 className="mt-10 font-display text-xl font-bold uppercase text-white">Utilizzi</h2>
    <ul className="mt-4 space-y-2">
      {product.uses.map((u) => (
        <li key={u} className="flex items-start gap-3 text-sm text-white/75">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {u}
        </li>
      ))}
    </ul>
    <ul className="mt-8 flex flex-wrap gap-2">
      {product.specs.map((s) => (
        <li key={s} className="rounded border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-white/60">{s}</li>
      ))}
    </ul>
    <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
      <QuoteButton product={product} />
      <p className="text-xs text-white/40">Nessun pagamento online: ti inviamo un preventivo personalizzato.</p>
    </div>
    <p className="mt-8 text-sm text-white/55">
      Serve anche la posa o la sostituzione?{" "}
      <Link to="/manutenzione-poligoni-di-tiro" data-testid="product-poligoni-link" className="text-brand hover:text-brand-hover">
        Scopri il servizio di manutenzione poligoni di tiro
      </Link>
    </p>
  </div>
);

const ProductView = ({ product }) => {
  const jsonLd = useMemo(() => productJsonLd(product), [product]);
  useSeo({ title: product.seo_title, description: product.seo_description, image: product.image }, jsonLd);
  const others = PRODUCTS.filter((p) => p.id !== product.id);

  return (
    <main data-testid="product-detail-page" className="pt-[116px] md:pt-[72px]">
      <section className="mx-auto max-w-[1600px] px-6 py-16 md:px-12 md:py-24">
        <nav data-testid="product-breadcrumb" className="mb-10 flex flex-wrap items-center gap-2 text-xs uppercase tracking-[0.18em] text-white/40">
          <Link to="/" className="hover:text-white">Home</Link> /
          <Link to="/shop" data-testid="breadcrumb-shop-link" className="hover:text-white">Shop</Link> /
          <span className="text-white/70">{product.name}</span>
        </nav>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-20">
          <Reveal><ProductImage product={product} /></Reveal>
          <Reveal delay={150}><ProductInfo product={product} /></Reveal>
        </div>
        <Link to="/shop" data-testid="back-to-shop-link" className="mt-14 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-brand hover:text-brand-hover">
          <ArrowLeft className="h-4 w-4" /> Torna allo SHOP
        </Link>
      </section>
      {others.length > 0 && (
        <section data-testid="related-products" className="border-t border-white/10 bg-surface">
          <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12">
            <Eyebrow>Altri prodotti</Eyebrow>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug);
  return product ? <ProductView product={product} /> : <Navigate to="/shop" replace />;
}
